import { Button, Icon, Input, Modal } from '@salesforce/design-system-react';
import { useEffect, useRef, useState } from 'react';

import type { IconCategory } from '../../types/Enum';
import type { ILookupOption } from '../../types/ILookup';

import './SearchModal.css';

const MIN_SEARCH_LENGTH = 2;
const MODAL_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 300;

// The "individual input layer" for modal searches: the one place responsible for excluding
// blank/too-short field values before a search is ever sent out, so the search functions in
// useLookupSearch.ts don't need to. Declared outside the component (not per-render) so its
// identity is stable for the `useEffect` below. Trims each value and keeps only entries at
// least MIN_SEARCH_LENGTH characters long.
const searchableFilters = <TField extends string>(
  candidate: Partial<Record<TField, string>>,
): Partial<Record<TField, string>> => {
  const result: Partial<Record<TField, string>> = {};
  for (const field of Object.keys(candidate) as TField[]) {
    const value = candidate[field]?.trim();
    if (value && value.length >= MIN_SEARCH_LENGTH) result[field] = value;
  }
  return result;
};

export interface SearchModalConfig<TRecord extends { Id: string }, TField extends string> {
  columns: ReadonlyArray<{ field: TField; label: string }>;
  getCellValue: (record: TRecord, field: TField) => string;
  listTitle?: string;
  search: (filters: Partial<Record<TField, string>>, limit: number) => Promise<TRecord[]>;
  searchFields?: ReadonlyArray<{ field: TField; label: string }>;
  searchOnOpen?: boolean;
  toOption: (record: TRecord) => ILookupOption;
}

interface SearchModalProps<TRecord extends { Id: string }, TField extends string> extends SearchModalConfig<
  TRecord,
  TField
> {
  iconCategory: IconCategory;
  iconName: string;
  isOpen: boolean;
  label: string;
  onRequestClose: () => void;
  onSelect: (value: ILookupOption) => void;
}

export const SearchModal = <TRecord extends { Id: string }, TField extends string>({
  columns,
  getCellValue,
  iconCategory,
  iconName,
  isOpen,
  label,
  listTitle,
  onRequestClose,
  onSelect,
  search,
  searchFields = columns,
  searchOnOpen = false,
  toOption,
}: SearchModalProps<TRecord, TField>) => {
  const [filters, setFilters] = useState<Partial<Record<TField, string>>>({});
  const [results, setResults] = useState<TRecord[]>([]);
  const [loading, setLoading] = useState(searchOnOpen);
  const [error, setError] = useState<string>();
  const sequenceRef = useRef(0);
  const hasSearchFilters = Object.keys(searchableFilters(filters)).length > 0;
  useEffect(() => {
    if (!isOpen) return;
    sequenceRef.current += 1;
    const { current } = sequenceRef;
    if (!hasSearchFilters && !searchOnOpen) return;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(undefined);
      void search(searchableFilters(filters), MODAL_LIMIT)
        .then(records => {
          if (sequenceRef.current === current) setResults(records);
        })
        .catch((reason: unknown) => {
          if (sequenceRef.current !== current) return;
          setError(reason instanceof Error ? reason.message : `${label} search failed`);
        })
        .finally(() => {
          if (sequenceRef.current === current) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      sequenceRef.current += 1;
    };
  }, [filters, hasSearchFilters, isOpen, label, search, searchOnOpen]);

  const handleClose = () => {
    sequenceRef.current += 1;
    setLoading(false);
    setError(undefined);
    onRequestClose();
  };

  const handleSelect = (record: TRecord) => {
    onSelect(toOption(record));
    handleClose();
  };

  return (
    <Modal
      className="search-lookup-modal"
      containerClassName="search-modal-container"
      contentClassName="search-modal-content slds-p-around_medium"
      footer={<Button label="Close" onClick={handleClose} />}
      headerClassName="search-modal-header"
      heading={`Search ${label}`}
      isOpen={isOpen}
      onRequestClose={handleClose}
      size="large"
    >
      <div>
        <div className="slds-grid slds-wrap slds-gutters_small slds-p-bottom_medium">
          {searchFields.map(({ field, label: fieldLabel }) => (
            <div
              className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-large-size_1-of-3 slds-p-bottom_x-small"
              key={field}
            >
              <Input
                label={fieldLabel}
                onChange={(_event, data) => {
                  setLoading(Object.keys(searchableFilters({ ...filters, [field]: data.value })).length > 0);
                  setError(undefined);
                  setResults([]);
                  setFilters(previous => ({ ...previous, [field]: data.value }));
                }}
                value={filters[field] ?? ''}
              />
            </div>
          ))}
        </div>
        {listTitle ? (
          <h2 className="slds-grid slds-grid_vertical-align-center slds-p-bottom_small slds-text-heading_small">
            <Icon category={iconCategory} containerClassName="slds-m-right_x-small" name={iconName} size="small" />
            <span>{listTitle}</span>
          </h2>
        ) : null}
        {error ? (
          <p className="slds-text-color_error slds-p-bottom_x-small" role="alert">
            {error}
          </p>
        ) : null}
        {loading ? (
          <p className="slds-p-bottom_x-small" role="status">
            Searching {label}…
          </p>
        ) : null}
        {!loading && !error && !hasSearchFilters && !searchOnOpen && results.length === 0 ? (
          <p className="slds-p-bottom_x-small">Enter at least two characters in a field to search {label}.</p>
        ) : null}
        {!loading && !error && (hasSearchFilters || searchOnOpen) && results.length === 0 ? (
          <p className="slds-p-bottom_x-small">No {label} records found.</p>
        ) : null}
        <div className="slds-scrollable_x">
          <table
            aria-label={`${label} results`}
            className="slds-table slds-table_bordered slds-table_cell-buffer slds-table_fixed-layout slds-table_striped"
          >
            <thead>
              <tr>
                {columns.map(({ field, label: fieldLabel }) => (
                  <th key={field} scope="col">
                    <div className="slds-truncate" title={fieldLabel}>
                      {fieldLabel}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map(record => (
                <tr
                  key={record.Id}
                  onClick={() => {
                    handleSelect(record);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelect(record);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  tabIndex={0}
                >
                  {columns.map(({ field }, index) => {
                    const value = getCellValue(record, field);
                    return (
                      <td key={field}>
                        {index === 0 ? (
                          <span className="slds-truncate" title={value || toOption(record).label}>
                            {value || toOption(record).label}
                          </span>
                        ) : (
                          <span className="slds-truncate" title={value}>
                            {value}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && results.length === MODAL_LIMIT ? (
          <p className="slds-p-top_small">Showing the first 100 results. Refine your search to narrow them down.</p>
        ) : null}
      </div>
    </Modal>
  );
};
